<template>
  <pre v-if="invoices.length" :inner-html.prop="controlTape"></pre>
</template>

<script>
import moment from 'moment'
import { parsePrice } from '@/utils/price'

export default {
  props: {
    invoices: {
      required: true,
      type: Array
    },
    invoiceHeader: {
      required: true,
      type: String
    },
    company: {
      required: true,
      type: Object
    },
    businessUnit: {
      required: true,
      type: Object
    },
    startDateTime: {
      required: true,
      type: String
    },
    endDateTime: {
      required: true,
      type: String
    }
  },
  data () {
    return {
    }
  },
  computed: {
    controlTape () {
      return this.headerHtml + this.invoicesHtml
    },
    headerHtml () {
      let html = ''
      const headerArr = [
        ...this.invoiceHeaderLines,
        this.printDate,
        ...this.startEndTime,
        '\n',
        ...this.businessUnitAndDevice,
        ...this.title
      ]
      headerArr.forEach(line => {
        html += line + '\n'
      })
      return html
    },
    invoicesHtml () {
      let html = ''
      this.invoices.forEach(invoice => {
        html += `${this.buyerHtml(invoice)}${this.invoiceNumberAndDate(invoice)}
Naziv                                 Količina
<u>        Cena        Pop.%    DDV %        Znesek</u>
${this.articlesHtml(invoice)}
${this.totalPrice(invoice)}
${this.paymentAndMoniker(invoice)}
------DDV------------Osnova----------Davek------
${this.taxLevelsHtml(invoice)}
${this.zoiAndEor(invoice)}
================================================
`
      })
      return html
    },

    invoiceHeaderLines () {
      if (this.invoiceHeader.length) {
        const lines = this.stripHtml(this.invoiceHeader).split('\n')
        lines.push('')
        return lines
      }
      return [
        ...this.splitTextOnSpace(this.company.name),
        ...this.splitTextOnSpace(`${this.company.address}, ${this.company.post_code} ${this.company.city}`),
        ...this.splitTextOnSpace(`${this.company.tax_bound ? 'ID za DDV: SI' : 'DAVČNA ŠT.: '}${this.company.tax_number}`),
        ...this.splitTextOnSpace(this.businessUnit.name),
        ...this.businessUnitAddress,
        ''
      ]
    },
    businessUnitAddress () {
      if (this.hasAllPeData) {
        const address = `${this.businessUnit.address} ${this.businessUnit.house_number}${this.businessUnit.house_number_addition || ''}, ${this.businessUnit.post_code} ${this.businessUnit.city}`
        return this.splitTextOnSpace(address)
      }
      return []
    },
    hasAllPeData () {
      return this.businessUnit.address && this.businessUnit.house_number && this.businessUnit.post_code && this.businessUnit.city
    },
    title () {
      return [
        '************************************************',
        '*                <b>Kontrolni trak</b>                *',
        '************************************************'
      ]
    },
    businessUnitAndDevice () {
      return [
        `Oznaka posl. prostora: ${this.businessUnit.business_premise_id}`,
        `          el. naprave: ${this.invoices[0].electronic_device_id}`
      ]
    },
    printDate () {
      return `Datum izpisa: ${moment().format('DD.MM.YYYY HH:mm')}`
    },
    startEndTime () {
      return [
        `Dokumenti od: ${moment(this.startDateTime).format('DD.MM.YYYY HH:mm')}`,
        `          do: ${moment(this.endDateTime).format('DD.MM.YYYY HH:mm')}`
      ]
    },
    taxNumber () {
      const label = this.company.tax_bound ? 'ID za DDV: SI' : 'DAVČNA ŠT.: '
      return `${label}${this.company.tax_number}`
    }
  },
  methods: {
    buyerHtml (invoice) {
      if (!invoice.buyerInfo) return ''
      const buyerArr = [
        `<b>Kupec:</b>`,
        ...this.splitTextOnSpace(invoice.buyerInfo.name),
        ...this.splitTextOnSpace(invoice.buyerInfo.address),
        ...this.splitTextOnSpace(`${invoice.buyerInfo.post_code} ${invoice.buyerInfo.city}`),
        `ID za DDV: ${invoice.buyerInfo.tax_number}`
      ]
      let html = ''
      buyerArr.forEach(line => {
        html += line + '\n'
      })
      return html
    },
    invoiceNumberAndDate (invoice) {
      const invoiceNum = this.padAfter(`RAČUN št.: ${invoice.number}`, 28)
      let html = `<b>${invoiceNum}    ${moment(invoice.date).format('DD.MM.YYYY HH:mm')}</b>`
      let refrence = null
      if (invoice.reverse_invoice) {
        refrence = this.padAfter(`VEZA: ${invoice.reverse_business_premise_id}-${invoice.reverse_electronic_device_id}-${invoice.reverse_invoice_number}`, 48)
      }
      if (invoice.reversed_invoice) {
        refrence = this.padAfter(`VEZA: ${invoice.reversed_business_premise_id}-${invoice.reversed_electronic_device_id}-${invoice.reversed_invoice_number}`, 48)
      }

      if (refrence) {
        html += `
<u>${refrence}</u>`
      } else {
        html = `<u>${html}</u>`
      }
      return html
    },
    totalPrice (invoice) {
      let price = this.padBefore(`${parsePrice(invoice.payment_amount)} EUR`, 37)
      return `<b>Za plačilo:${price}</b>`
    },
    paymentAndMoniker (invoice) {
      let payment = this.padAfter(this.paymentMethod(invoice), 24)
      let moniker = this.padBefore(invoice.moniker, 24)
      return `${payment}${moniker}`
    },
    zoiAndEor (invoice) {
      let html = `ZOI: ${invoice.zoi || ''}
EOR: ${invoice.invoice_id || ''}`
      if (invoice.registration_date) {
        html += `
Račun fiskaliziran: ${moment(invoice.registration_date).format('DD.MM.YYYY HH:mm:ss')}`
      }
      return html
    },
    articlesHtml (invoice) {
      const lines = this.articles(invoice)
      let html = ''
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        if (i === lines.length - 1) {
          html += '<u>' + line + '</u>\n'
        } else {
          html += line + '\n'
        }
      }
      return html
    },
    articles (invoice) {
      let articles = []
      invoice.articles.forEach(article => {
        articles = this.pushArticleNameAndQuantity(article, articles)
        const perUnit = this.padBefore(parsePrice(this.articleAmountPerUnit(article)), 12)
        let discount = article.discount ? article.discount : ''
        discount = this.padBefore(discount, 10)
        const taxLevel = this.padBefore(this.floatString(article.tax_level_value), 12)
        const total = this.padBefore(parsePrice(article.total_amount), 14)
        articles.push(`${perUnit}${discount}${taxLevel}${total}`)
      })
      return articles
    },
    pushArticleNameAndQuantity (article, articles) {
      let articleName = article.invoice_name
      let quantity = `${this.floatString(article.quantity)}x  `
      const articleSpace = 48 - quantity.length - 2
      if (articleName.length > articleSpace) {
        // multiline
        const articleNameLines = this.splitTextOnSpace(articleName, articleSpace)
        articles.push(`${this.padAfter(articleNameLines[0], articleSpace + 2)}${quantity}`)
        for (let i = 1; i < articleNameLines.length; i++) {
          articles.push(this.padAfter(`${articleNameLines[i]}`, 48))
        }
      } else {
        articleName = this.padAfter(articleName, articleSpace + 2)
        articles.push(`${articleName}${quantity}`)
      }
      return articles
    },
    taxLevelsHtml (invoice) {
      const lines = this.taxLevels(invoice)
      let html = ''
      lines.forEach(line => {
        html += line + '\n'
      })
      return html
    },
    taxLevels (invoice) {
      const levels = []
      invoice.taxGroups.forEach(group => {
        let taxLevel = this.padBefore(`${group.tax_level_value} %`, 11)
        let taxable = this.padBefore(`${parsePrice(group.taxable_amount)}`, 16)
        let tax = this.padBefore(`${parsePrice(group.tax_amount)}`, 15)
        levels.push(`${taxLevel}${taxable}${tax}      `)
      })
      return levels
    },
    paymentMethod (invoice) {
      return invoice.print_payment_type === 1 ? 'Gotovinsko' : 'Negotovinsko'
    },
    floatString (number) {
      let num = Number.parseFloat((number + '').replace(',', '.')).toFixed(2).replace('.', ',')
      const numLen = num.length
      if (num[numLen - 1] === '0') {
        num = num[numLen - 2] === '0' ? num.substring(0, numLen - 3) : num.substring(0, numLen - 1)
      }
      return num
    },
    articleAmountPerUnit (article) {
      return this.articleAmountWithoutDiscount(article) / article.quantity
    },
    articleAmountWithoutDiscount (article) {
      if (!article.discount) return article.total_amount
      return (article.total_amount * 100) / (100 - article.discount)
    },
    splitTextOnSpace (text, columns = 48) {
      if (text.length <= columns) return [text]
      let currentSpace = columns
      let prevSpace = null
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          if (currentSpace) prevSpace = currentSpace
          currentSpace = i
          if (currentSpace >= columns) {
            const firstPart = text.substring(0, prevSpace)
            const secondPart = text.substring(prevSpace + 1)
            if (secondPart.length > columns) {
              const splitedAgain = this.splitTextOnSpace(secondPart, columns)
              return [
                firstPart,
                ...splitedAgain
              ]
            } else {
              return [
                firstPart,
                secondPart
              ]
            }
          }
        }
      }
      return [
        text.substring(0, currentSpace),
        text.substring(currentSpace + 1)
      ]
    },
    padBefore (text, length) {
      for (let i = text.length; i < length; i++) {
        text = ' ' + text
      }
      return text
    },
    padAfter (text, length) {
      for (let i = text.length; i < length; i++) {
        text += ' '
      }
      return text
    },
    stripHtml (html) {
      var tmp = document.createElement('DIV')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || ''
    }
  }
}
</script>
